<template>
  <section :class="['two-column-info', { 'is-not-expanded': !isExpanded }]">
    <div class="two-column-info__container">
      <!-- Left Column - Big Text -->
      <div class="two-column-info__left">
        <big-text :text="title" />
      </div>

      <!-- Right Column - Description and CTA -->
      <div class="two-column-info__right">
        <div class="two-column-info__content">
          <div
            ref="descriptionRef"
            class="two-column-info__description"
            v-html="description">
          </div>

          <div
            v-if="showCta"
            class="two-column-info__cta">
            <cta
              :text="currentCtaText"
              variant="text"
              color="red"
              :show-icon="false"
              @click="handleCtaClick" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
  import {
    computed,
    nextTick,
    onMounted,
    ref
  } from 'vue'

  import BigText from './BigText.vue'
  import Cta from './Cta.vue'

  // Props
  const props = defineProps({
    title: {
      type: String,
      required: true,
      validator: value => typeof value === 'string' && value.trim().length > 0
    },
    description: {
      type: String,
      required: true,
      validator: value => typeof value === 'string' && value.trim().length > 0
    },
    ctaText: {
      type: String,
      default: 'LEGGI DI PIÙ'
    }
  })

  // Emits
  const emit = defineEmits([ 'cta-click' ])

  // Reactive state
  const descriptionRef = ref(null)
  const isExpanded = ref(false)
  const showCta = ref(false)
  const maxHeight = ref('auto')

  // Computed
  const currentCtaText = computed(() => {
    return isExpanded.value ? 'LEGGI MENO' : (props.ctaText || 'LEGGI DI PIÙ')
  })

  // Constants
  const MAX_LINES = 5
  const LINE_HEIGHT = 28.8 // Approximate line height in pixels (1.6 * 18px font size)

  // Methods
  const checkTextHeight = async () => {
    await nextTick()

    if (!descriptionRef.value) return

    // Reset styles to measure natural height
    descriptionRef.value.style.maxHeight = 'none'
    descriptionRef.value.style.overflow = 'visible'

    const naturalHeight = descriptionRef.value.scrollHeight
    const maxAllowedHeight = MAX_LINES * LINE_HEIGHT

    if (naturalHeight > maxAllowedHeight) {
      showCta.value = true
      maxHeight.value = `${maxAllowedHeight}px`

      // Apply truncation styles
      descriptionRef.value.style.maxHeight = maxHeight.value
      descriptionRef.value.style.overflow = 'hidden'
    } else {
      showCta.value = false
      maxHeight.value = 'auto'
    }
  }

  const toggleExpansion = () => {
    isExpanded.value = !isExpanded.value

    if (isExpanded.value) {
      descriptionRef.value.style.maxHeight = `${descriptionRef.value.scrollHeight}px`
    } else {
      descriptionRef.value.style.maxHeight = maxHeight.value
    }
  }

  const handleCtaClick = event => {
    toggleExpansion()

    emit('cta-click', {
      event,
      expanded: isExpanded.value,
      timestamp: new Date().toISOString()
    })
  }

  // Lifecycle
  onMounted(() => {
    checkTextHeight()
  })
</script>

<style lang="scss" scoped>
.two-column-info {
  padding: var(--space-xxl) 0;

  &__container {
    max-width: var(--grid-max-width);
    margin: 0 auto;
    padding: 0 var(--grid-margin);
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-xl);
    align-items: start;

    @include up-to-tablet {
      padding: 0 var(--space-s);
      grid-template-columns: 1fr;
      gap: var(--space-l);
    }
  }

  &__left {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
  }

  &__right {
    display: flex;
    flex-direction: column;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 48px;
  }

  &__description {
    font-family: var(--font-family-poppins);
    font-size: var(--typography-size-s);
    font-weight: var(--font-weight-book);
    line-height: 1.6;
    color: var(--color-dark-grey);
    margin: 0;
    transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    padding-bottom:18px;

    // Style HTML content from backend
    :deep(p) {
      margin: 0 0 var(--space-s) 0;

      &:last-child {
        margin-bottom: 0;
      }
    }

    :deep(strong) {
      font-weight: var(--font-weight-medium);
    }

    :deep(em) {
      font-style: italic;
    }

    :deep(a) {
      color: var(--color-red);
      text-decoration: underline;

      &:hover {
        opacity: 0.8;
      }
    }

    .is-not-expanded & {
      &::after {
        content: "";
        position: absolute;
        width: 100%;
        height: 30px;
        left: 0;
        bottom: 0;
        background-image: linear-gradient(0deg, #fff 50%, rgb(255, 255, 255, 0) 100%);
      }
    }

    @include up-to-tablet {
      font-size: var(--typography-size-xs);
    }
  }

  &__cta {
    display: flex;
    justify-content: flex-start;
  }
}
</style>
